import { saveAs } from 'file-saver';

import type { Company } from '../../../../@types/Company';
import type { ReferentCampaignNumber } from '../../../../utils/api';
import {
  deleteCompanyReferentCampaignDocument,
  getCompanyReferentCampaignDocument,
  postCompanyReferentCampaignDocument,
} from '../../../../utils/api';
import { JWTAuthorization } from '../../../../utils/JWTHeader';

const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;
const OVERSIZED_DOCUMENT_MESSAGE = 'Le document dépasse la taille maximale autorisée (10 Mo).';
const ALLOWED_MIME_TYPES = new Set<string>([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'] as const;

export type CustomDocFile = {
  filename: string;
  relativePath: string;
};

/**
 * Validate the client-side constraints applied to a "Modèle 5" upload. The
 * server enforces the same constraints, but we surface them ahead of time so
 * the UI can give immediate feedback.
 */
export const validateCustomDocFile = (file: File): string | null => {
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    return OVERSIZED_DOCUMENT_MESSAGE;
  }
  const lowerName = file.name.toLowerCase();
  const hasAllowedExtension = ALLOWED_EXTENSIONS.some(ext => lowerName.endsWith(ext));
  if (!ALLOWED_MIME_TYPES.has(file.type) && !hasAllowedExtension) {
    return 'Seuls les fichiers PDF, DOC ou DOCX sont acceptés.';
  }
  return null;
};

/**
 * Parse a non-OK upload response and return a human-readable French message.
 *
 * The server may answer with:
 * - A JSON body coming from Symfony (`{ error: '…' }` or `{ errors: [...] }`).
 * - A plain HTML 413 page coming from the reverse proxy when the request body
 *   exceeds `client_max_body_size` (the request never reaches PHP).
 * - A PHP fatal returning `UPLOAD_ERR_INI_SIZE`/`UPLOAD_ERR_FORM_SIZE` style
 *   messages embedded in the form validation payload.
 */
const buildUploadErrorMessage = async(response: Response): Promise<string> => {
  if (response.status === 413) {
    return OVERSIZED_DOCUMENT_MESSAGE;
  }

  const rawBody = await response.text().catch(() => '');
  let parsed: unknown = null;
  try {
    parsed = rawBody ? JSON.parse(rawBody) : null;
  } catch {
    parsed = null;
  }

  const errorPayload = parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
  const directMessage = typeof errorPayload?.error === 'string'
    ? errorPayload.error
    : typeof errorPayload?.message === 'string'
      ? errorPayload.message
      : null;

  // Symfony's `BadRequestFromValidatorResponse` shape: `{ errors: { file: [...] } }`.
  let validationMessage: string | null = null;
  const errorsBag = errorPayload?.errors;
  if (errorsBag && typeof errorsBag === 'object') {
    for (const value of Object.values(errorsBag as Record<string, unknown>)) {
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
        validationMessage = value[0];
        break;
      }
      if (typeof value === 'string') {
        validationMessage = value;
        break;
      }
    }
  }

  const combined = `${directMessage ?? ''} ${validationMessage ?? ''} ${rawBody}`.toLowerCase();
  const oversizedHints = [
    'request entity too large',
    'too large',
    'upload_max_filesize',
    'post_max_size',
    '10 mo',
    '10mo',
    '10 mb',
  ];
  const matchesOversized = oversizedHints.some(hint => combined.includes(hint))
    || (combined.includes('trop') && combined.includes('volumineu'));
  if (matchesOversized) {
    return OVERSIZED_DOCUMENT_MESSAGE;
  }

  return directMessage ?? validationMessage ?? 'Échec de l\'import du document.';
};

/**
 * Extract the filename portion from a public-relative document path.
 * Returns the path itself when no slash is present.
 */
export const extractCustomDocFilename = (relativePath: string | undefined | null): string => {
  if (!relativePath) { return ''; }
  const segments = relativePath.split('/');
  return segments[segments.length - 1] ?? relativePath;
};

/**
 * Build the `CustomDocFile` view of the saved upload for a given campaign,
 * reading the relative path from the company's branding settings.
 */
export const getCustomDocForCampaign = (
  customDocs: Partial<Record<'1' | '2' | '3', string>> | undefined,
  campaign: ReferentCampaignNumber,
): CustomDocFile | null => {
  const relativePath = customDocs?.[String(campaign) as '1' | '2' | '3'];
  if (!relativePath) { return null; }
  return { filename: extractCustomDocFilename(relativePath), relativePath };
};

export const uploadCustomDoc = async(
  companyId: number,
  campaign: ReferentCampaignNumber,
  file: File,
  jwt: string | null,
): Promise<Company> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(postCompanyReferentCampaignDocument(companyId, campaign), {
    method: 'POST',
    headers: { Authorization: JWTAuthorization(jwt) },
    body: formData,
  });
  if (!response.ok) {
    throw new Error(await buildUploadErrorMessage(response));
  }
  return response.json() as Promise<Company>;
};

export const deleteCustomDoc = async(
  companyId: number,
  campaign: ReferentCampaignNumber,
  jwt: string | null,
): Promise<Company> => {
  const response = await fetch(deleteCompanyReferentCampaignDocument(companyId, campaign), {
    method: 'DELETE',
    headers: { Authorization: JWTAuthorization(jwt) },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error((errorData && (errorData.error || errorData.message)) || 'Échec de la suppression du document.');
  }
  return response.json() as Promise<Company>;
};

/**
 * Download the uploaded document for a given campaign, preserving the
 * original filename returned by the server (or falling back to the stored
 * relative path).
 */
export const downloadCustomDoc = async(
  companyId: number,
  campaign: ReferentCampaignNumber,
  jwt: string | null,
  fallbackFilename: string,
): Promise<void> => {
  const response = await fetch(getCompanyReferentCampaignDocument(companyId, campaign), {
    method: 'GET',
    headers: { Authorization: JWTAuthorization(jwt) },
  });
  if (!response.ok) {
    throw new Error('Le document est introuvable.');
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition') ?? '';
  const filenameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : fallbackFilename;
  saveAs(blob, filename);
};
